// SPDX-License-Identifier: AGPL-3.0-or-later

import {AttachmentGridItem} from '@app/features/channel/components/embeds/attachments/AttachmentGridItem';
import {planInlineMediaLayout} from '@app/features/channel/components/InlineMediaLayout';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {getMosaicMediaDimensions} from '@app/features/messaging/utils/MediaDimensionConfig';
import styles from '@app/features/theme/styles/AttachmentLayoutGrid.module.css';
import type {MessageAttachment} from '@fluxer/schema/src/domains/message/MessageResponseSchemas';
import {observer} from 'mobx-react-lite';
import type {CSSProperties, FC} from 'react';
import {useMemo} from 'react';

export interface AttachmentLayoutGridProps {
	attachments: ReadonlyArray<MessageAttachment>;
	message?: Message;
	isPreview?: boolean;
	snapshotIndex?: number;
}

interface InlineMediaGridStyle extends CSSProperties {
	'--inline-media-cols': number;
}

export const AttachmentLayoutGrid: FC<AttachmentLayoutGridProps> = observer(
	({attachments, message, isPreview, snapshotIndex}) => {
		const mosaicWidth = getMosaicMediaDimensions().maxWidth;
		const layout = useMemo(
			() =>
				planInlineMediaLayout(
					attachments.map((attachment) => ({
						width: attachment.width ?? 0,
						height: attachment.height ?? 0,
					})),
					{maxWidth: mosaicWidth},
				),
			[attachments, mosaicWidth],
		);
		const gridStyle: InlineMediaGridStyle = {
			'--inline-media-cols': layout.columns,
		};
		return (
			<div
				className={styles.mediaGrid}
				style={gridStyle}
				data-flx="channel.embeds.attachments.attachment-layout-grid.media-grid"
			>
				{attachments.map((attachment, index) => {
					const tile = layout.tiles[index];
					if (tile == null) {
						return null;
					}
					return (
						<AttachmentGridItem
							key={attachment.id}
							attachment={attachment}
							tile={tile}
							message={message}
							mediaAttachments={attachments}
							isPreview={isPreview}
							snapshotIndex={snapshotIndex}
							data-flx="channel.embeds.attachments.attachment-layout-grid.attachment-grid-item"
						/>
					);
				})}
			</div>
		);
	},
);
